import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import {
  customerLogin,
  customerLogout,
  customerSignup,
  customerMe,
} from "@/lib/api";

/* CustomerAuthContext — separate from admin AuthContext.
 * state.customer:
 *   null      → still checking session on mount
 *   object    → logged-in customer
 *   false     → confirmed signed-out / not authenticated
 */
const CustomerAuthContext = createContext(null);

export function CustomerAuthProvider({ children }) {
  const [customer, setCustomer] = useState(null);
  const [bootstrapped, setBootstrapped] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const me = await customerMe();
      setCustomer(me);
    } catch {
      setCustomer(false);
    } finally {
      setBootstrapped(true);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const data = await customerLogin(email, password);
    setCustomer(data);
    return data;
  }, []);

  const signup = useCallback(async (payload) => {
    const data = await customerSignup(payload);
    setCustomer(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await customerLogout();
    } finally {
      setCustomer(false);
    }
  }, []);

  return (
    <CustomerAuthContext.Provider
      value={{ customer, bootstrapped, login, signup, logout, refresh }}
    >
      {children}
    </CustomerAuthContext.Provider>
  );
}

export function useCustomerAuth() {
  const ctx = useContext(CustomerAuthContext);
  if (!ctx)
    throw new Error("useCustomerAuth must be used inside <CustomerAuthProvider>");
  return ctx;
}
